// src/pages/Proof.jsx
import React, { useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  TextField,
  Slider,
  Container,
} from "@mui/material";
import { Link } from "react-router-dom";
import { pack } from "../api/proof";
import LoafBadge from "../components/LoafBadge";

export default function Proof() {
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Demo inputs
  const [query, setQuery] = useState(
    "Write a haiku about lockfiles in software development."
  );
  const [docs, setDocs] = useState(
    "Versions pinned tight,\nDependencies frozen still,\nLockfile guards the path."
  );
  const [budget, setBudget] = useState(300);

  const handleRunPack = async () => {
    setIsRunning(true);
    setError(null);
    setResult(null);

    try {
      const packResult = await pack({
        query,
        docs,
        budget,
      });
      setResult(packResult);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100vw",
        background: (theme) => theme.palette.background.gradient,
        backgroundAttachment: "fixed",
        py: 8,
        px: { xs: 2, sm: 4, md: 6 },
      }}
    >
      <Container maxWidth="lg">
        {/* Hero Section */}
        <Box sx={{ textAlign: "center", mb: 8 }}>
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: "3rem", md: "4.5rem" },
              mb: 4,
              lineHeight: 1.1,
              background: "linear-gradient(135deg, #ffffff 0%, #79d1c3 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            LoafProof
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "1.5rem", md: "2.5rem" },
              mb: 4,
              color: "rgba(255, 255, 255, 0.8)",
              fontWeight: 300,
            }}
          >
            Smart document chunking & context packing
          </Typography>

          {/* Feature Bullets */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={4}
            sx={{
              justifyContent: "center",
              mb: 6,
              maxWidth: "800px",
              mx: "auto",
            }}
          >
            <Box sx={{ textAlign: "center", flex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  color: "#79d1c3",
                  mb: 1,
                  fontWeight: 600,
                }}
              >
                Smart Chunking
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "rgba(255, 255, 255, 0.7)",
                  lineHeight: 1.6,
                }}
              >
                Intelligent document segmentation with overlap handling for
                optimal context retention.
              </Typography>
            </Box>
            <Box sx={{ textAlign: "center", flex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  color: "#79d1c3",
                  mb: 1,
                  fontWeight: 600,
                }}
              >
                MMR Selection
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "rgba(255, 255, 255, 0.7)",
                  lineHeight: 1.6,
                }}
              >
                Maximal Marginal Relevance for diverse, relevant results that
                avoid redundancy.
              </Typography>
            </Box>
            <Box sx={{ textAlign: "center", flex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  color: "#79d1c3",
                  mb: 1,
                  fontWeight: 600,
                }}
              >
                Token Budget
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "rgba(255, 255, 255, 0.7)",
                  lineHeight: 1.6,
                }}
              >
                Precise token counting and budget management for efficient
                context utilization.
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* Demo Section */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h4"
            sx={{
              textAlign: "center",
              mb: 4,
              color: "#ffffff",
              fontWeight: 500,
            }}
          >
            Try Context Packing
          </Typography>

          <Card
            sx={{
              background: "rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              borderRadius: "16px",
              maxWidth: 800,
              mx: "auto",
              p: 3,
            }}
          >
            <CardContent>
              <Stack spacing={3}>
                <TextField
                  label="Query"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      color: "white",
                      "& fieldset": {
                        borderColor: "rgba(255, 255, 255, 0.3)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(255, 255, 255, 0.5)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#79d1c3",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "rgba(255, 255, 255, 0.7)",
                    },
                  }}
                />

                <TextField
                  label="Documents"
                  value={docs}
                  onChange={(e) => setDocs(e.target.value)}
                  fullWidth
                  multiline
                  rows={4}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      color: "white",
                      "& fieldset": {
                        borderColor: "rgba(255, 255, 255, 0.3)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(255, 255, 255, 0.5)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#79d1c3",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "rgba(255, 255, 255, 0.7)",
                    },
                  }}
                />

                <Box>
                  <Typography
                    variant="body2"
                    sx={{ color: "rgba(255, 255, 255, 0.7)", mb: 1 }}
                  >
                    Token Budget: {budget}
                  </Typography>
                  <Slider
                    value={budget}
                    onChange={(e, newValue) => setBudget(newValue)}
                    min={100}
                    max={1000}
                    step={50}
                    sx={{
                      color: "#79d1c3",
                      "& .MuiSlider-thumb": {
                        backgroundColor: "#79d1c3",
                      },
                      "& .MuiSlider-track": {
                        backgroundColor: "#79d1c3",
                      },
                    }}
                  />
                </Box>

                <Button
                  variant="contained"
                  onClick={handleRunPack}
                  disabled={isRunning}
                  sx={{
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)",
                    },
                    py: 1.5,
                  }}
                  fullWidth
                >
                  {isRunning ? (
                    <>
                      <CircularProgress
                        size={16}
                        sx={{ mr: 1, color: "white" }}
                      />
                      Packing Context...
                    </>
                  ) : (
                    "Pack Context"
                  )}
                </Button>

                {result && (
                  <Alert
                    severity="success"
                    sx={{
                      background: "rgba(76, 175, 80, 0.2)",
                      color: "white",
                      "& .MuiAlert-icon": { color: "#51c4a0" },
                    }}
                  >
                    <Typography variant="body2">
                      Context packed successfully!
                      {result.prompt && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Packed Prompt:
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: "monospace",
                              backgroundColor: "rgba(0, 0, 0, 0.2)",
                              p: 1,
                              borderRadius: 1,
                              mt: 0.5,
                            }}
                          >
                            {result.prompt.length > 200
                              ? `${result.prompt.slice(0, 200)}...`
                              : result.prompt}
                          </Typography>
                        </Box>
                      )}
                    </Typography>
                  </Alert>
                )}

                {error && (
                  <Alert
                    severity="error"
                    sx={{
                      background: "rgba(244, 67, 54, 0.2)",
                      color: "white",
                      "& .MuiAlert-icon": { color: "#f44336" },
                    }}
                  >
                    <Typography variant="body2">Error: {error}</Typography>
                  </Alert>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Box>

        {/* Performance Badges */}
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <Stack direction="row" spacing={2} justifyContent="center">
            <LoafBadge badgeContent="500ms" size="small">
              <Button variant="outlined" size="small">
                Pack Speed
              </Button>
            </LoafBadge>
            <LoafBadge badgeContent="95%" size="small">
              <Button variant="outlined" size="small">
                Relevance
              </Button>
            </LoafBadge>
            <LoafBadge badgeContent="Smart" size="small">
              <Button variant="outlined" size="small">
                Chunking
              </Button>
            </LoafBadge>
          </Stack>
        </Box>

        {/* Back to Home */}
        <Box sx={{ textAlign: "center" }}>
          <Button
            variant="outlined"
            component={Link}
            to="/"
            sx={{
              borderRadius: 999,
              px: 3,
              borderColor: "rgba(255, 255, 255, 0.2)",
              color: "#ffffff",
              "&:hover": {
                borderColor: "rgba(255, 255, 255, 0.3)",
                backgroundColor: "rgba(255, 255, 255, 0.02)",
              },
            }}
          >
            Back to Home
          </Button>
        </Box>
      </Container>
    </Box>
  );
}
